import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useMemo, useState } from 'react';
import { Textarea } from '../../../ui/form/Textarea';
import timeawayApi from '../../../network/timeaway';

interface BulkApproveModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  ids: string[];
  onApproveSuccess?: () => void;
}

export default function BulkApproveModal({
  open,
  setOpen,
  ids,
  onApproveSuccess,
}: BulkApproveModalProps) {
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const count = useMemo(() => ids.length, [ids.length]);

  const handleApprove = async () => {
    if (!ids.length) return;
    try {
      setIsSubmitting(true);
      await timeawayApi.bulkApproveTimeawayRequests(ids, note || undefined);
      setOpen(false);
      setNote('');
      onApproveSuccess?.();
    } catch (error: any) {
      console.error('Failed to bulk approve requests:', error);
      alert(
        `Failed to approve requests. ${
          error.response?.data?.message || error.message
        }`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center ">
          <DialogPanel
            transition
            className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in "
          >
            <div className="bg-white px-6 pt-2">
              <div className="flex flex-col">
                <div className="mt-4 flex justify-between">
                  <DialogTitle as="h6" className="font-semibold text-gray-900">
                    Approve {count} request{count === 1 ? '' : 's'}
                  </DialogTitle>
                  <button onClick={() => setOpen(false)} aria-label="Close dialog">
                    <XMarkIcon className="w-4" />
                  </button>
                </div>
                <div className="mt-3">
                  <p className="text-sm text-gray-600">
                    All selected employees will receive a notification about their
                    request being approved.
                  </p>
                </div>
                <div className="mt-3 flex flex-col gap-1">
                  <p className="font-semibold">Comment (optional)</p>
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional approval note for the employee(s)."
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-row justify-end gap-3 px-6 py-5">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-1"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                className="rounded-lg bg-[#15803D] px-4 py-1 text-white"
                disabled={isSubmitting || !ids.length}
              >
                {isSubmitting ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

